import React from "react";
import { usePlan } from "@/_actions/authActions";
import { useProfile } from "@/_actions/userActions";
import usePlanStore from "@/stores/planStore";
import apiClient from "@/utils/apiClient";
import { PlanCard } from "@/pages/particular-signup/select_plan/plans";
import EmbeddedCheckoutComponent from "@/pages/particular-signup/select_plan/EmbeddedCheckout";
import { getOwnerPlans, BILLING_INTERVALS } from "@/pages/signup/pro/owner-plans";
import BillingIntervalSelector from "@/pages/signup/pro/billing-interval-selector";
import {
  daysLeftPhrase,
  formatFullDate,
  formatShortDate,
  getRenewalInfo,
  isContractExpired,
} from "@/utils/contract";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

const formatLimit = (value) => {
  if (value === -1) return "Ilimitado";
  if (value === null || value === undefined) return "No disponible";
  return `${value}`;
};

export default function Subscription() {
  const storedPlan = usePlanStore((state) => state.plan);
  const storedContract = usePlanStore((state) => state.contract);
  const storedContractStatus = usePlanStore((state) => state.contractStatus);
  const [loading, setLoading] = React.useState(false);
  const { data: profile, isLoading, mutate: mutateProfile, error } = useProfile();
  const { data: planData, isLoading: isPlanLoading, error: planError } = usePlan();
  const plan = storedPlan || planData?.plan;
  const contract = storedContract || planData?.contract;
  const contractStatus = storedContractStatus || planData?.contract_status;
  const canManageSubscription = planData?.can_manage_subscription === true;
  const [selectedPlanId, setSelectedPlanId] = React.useState(null);
  const [billingInterval, setBillingInterval] = React.useState(BILLING_INTERVALS.MONTHLY);
  const [showCheckout, setShowCheckout] = React.useState(false);
  const ownerPlans = getOwnerPlans(billingInterval);
  const selectedPlan = ownerPlans.find((candidate) => candidate.idPlan === selectedPlanId);

  const handleSelectPlan = (planId) => {
    setSelectedPlanId(planId);
    setShowCheckout(true);
  };

  const handleBackToPlans = () => {
    setShowCheckout(false);
  };

  const getStripeSessionUrl = async () => {
    setLoading(true);
    try {
      const session = await apiClient.post("/clients/create-customer-portal-session");
      const stripeSessionUrl = session?.data?.url;
      if (typeof stripeSessionUrl !== "string" || stripeSessionUrl.length === 0) {
        throw new Error("Customer portal session response missing url");
      }
      window.open(stripeSessionUrl, "_blank");
    } catch (err) {
      $log.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (isLoading || isPlanLoading) {
    return <div>Loading...</div>;
  }

  if (error || planError) {
    return <div>Error: {(error || planError).message}</div>;
  }

  const expired = isContractExpired(contractStatus);
  const renewalInfo = expired ? getRenewalInfo(contract?.end_date) : null;
  const renewalHelper = expired
    ? renewalInfo
      ? `${daysLeftPhrase(renewalInfo.daysLeft)} para renovar${
          renewalInfo.daysLeft > 0 ? ` (antes del ${formatShortDate(renewalInfo.deadline)})` : ""
        }`
      : "Renueva tu plan para conservar el acceso"
    : null;

  const stats = [
    { name: "Plan", stat: plan?.name || "No disponible" },
    {
      name: "Propiedades disponibles para publicar",
      stat: formatLimit(plan?.plan_addons?.properties_quantity),
    },
    {
      name: "Usuarios disponibles",
      stat: formatLimit(plan?.plan_addons?.client_promoters),
    },
    contract?.end_date
      ? {
          name: expired ? "Tu plan venció el" : "Siguiente fecha de renovación",
          stat: formatFullDate(contract.end_date) || `${contract.end_date}`,
          helper: renewalHelper,
          highlight: expired,
        }
      : null,
  ].filter(Boolean);

  return (
    <div>
      <div className="border-b border-zinc-300 pb-3 ">
        <div className="ml-12 border-l border-zinc-300 pl-4">
          <h1 className="text-2xl font-bold leading-tight tracking-tight text-zinc-800">
            Subscripción
          </h1>
        </div>
      </div>
      <div>
        <div className="container py-20 space-y-10 px-3">
          <div>
            <p className="text-xl text-gray-500  text-pretty font-regular">
              Administra tu suscripción a Casas y Terrenos PRO.<br></br> Puedes contratar y pausar
              tu suscripción en cualquier momento.
            </p>
          </div>

          <div className="space-y-5">
            <div className="relative">
              <div aria-hidden="true" className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-start">
                <span className="bg-white pr-3 text-base font-semibold text-gray-900">
                  Información sobre tu plan actual
                </span>
              </div>
            </div>
            <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
              {stats.map((item) => (
                <div
                  key={item.name}
                  className={cn(
                    "overflow-hidden rounded-lg bg-white px-4 py-5 shadow-xs sm:p-6",
                    item.highlight && "bg-amber-50 ring-1 ring-amber-200",
                  )}
                >
                  <dt
                    className={cn(
                      "truncate text-sm font-medium text-gray-500",
                      item.highlight && "text-amber-700",
                    )}
                  >
                    {item.name}
                  </dt>
                  <dd
                    className={cn(
                      "mt-1 text-3xl font-semibold tracking-tight text-gray-900 capitalize",
                      item.highlight && "text-amber-900",
                    )}
                  >
                    {item.stat}
                  </dd>
                  {item.helper ? (
                    <p className="mt-1 text-sm font-medium text-amber-700 normal-case">
                      {item.helper}
                    </p>
                  ) : null}
                </div>
              ))}
            </dl>

            {canManageSubscription ? (
              <div>
                <button
                  disabled={loading}
                  onClick={getStripeSessionUrl}
                  type="button"
                  className="text-pretty flex rounded-md bg-gray-900 text-white px-3.5 py-2.5 text-sm
                hover:text-gray-900 shadow-2xs ring-1 ring-gray-300 ring-inset hover:bg-white
                disabled:bg-gray-300 disabled:text-gray-500"
                >
                  {loading ? <Loader2 className="animate-spin" /> : null}
                  <span>Administrar subscripción</span>
                </button>
              </div>
            ) : null}
          </div>
          <div className="space-y-5">
            {profile?.client && profile?.role === "owner" && plan?.name === "starter" ? (
              <>
                <div className="relative">
                  <div aria-hidden="true" className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-start">
                    <span className="bg-white pr-3 text-base font-semibold text-gray-900">
                      Adquiere un plan
                    </span>
                  </div>
                </div>

                <div className="mt-5">
                  <p className="text-gray-500 max-w-[500px]  text-pretty font-regular">
                    Adquiere un plan de pago para poder publicar todo tu inventario y acceder a
                    todas las funcionalidades de Casas y Terrenos PRO.
                  </p>
                </div>
                {showCheckout && selectedPlan ? (
                  <div className="w-full flex items-center justify-center">
                    <EmbeddedCheckoutComponent
                      key={`${selectedPlanId}-${billingInterval}`}
                      priceId={selectedPlan.priceId}
                      onBack={handleBackToPlans}
                      checkoutType="subscription"
                    />
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl font-semibold mb-6 text-center">
                      Elige un plan para comenzar
                    </h2>
                    <div className="flex justify-center mb-6">
                      <BillingIntervalSelector
                        value={billingInterval}
                        onValueChange={setBillingInterval}
                      />
                    </div>
                    <div className="grid gap-6 justify-items-center grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {ownerPlans
                        .filter((subscriptionPlan) => subscriptionPlan.priceId)
                        .map((subscriptionPlan) => (
                          <PlanCard
                            key={subscriptionPlan.id}
                            plan={subscriptionPlan}
                            selectedPlan={selectedPlanId}
                            onSelectPlan={handleSelectPlan}
                          />
                        ))}
                    </div>
                  </>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
